/**
 * Terminkonflikte (Benjamin, 03.10.2026): ist eine Person schon in einem
 * anderen Termin eingeplant, antwortet der Server mit 409 und den Konflikten —
 * je Konflikt die Lösungen, die geometrisch passen (hetzner/webhook/termin_konflikt.py).
 * Kontor fragt, wie aufgelöst werden soll, und schickt dieselbe Anfrage mit
 * `konflikt_loesungen` noch einmal. „Ohne" nimmt die Person aus dem neuen
 * Termin — das erledigt die Oberfläche selbst.
 */
import { ApiError } from "@vimaana/ui";

export type KonfliktArt = "alt_luecke" | "alt_ende" | "alt_anfang" | "neu_luecke" | "neu_ende" | "neu_anfang" | "doppelt" | "trotzdem";

export type Konflikt = {
  slot_id: string; termin_id: string; person_id: string; person_typ: string; person_name: string;
  bezeichnung: string; start: string; end: string; neu_start: string; neu_end: string; moeglich: KonfliktArt[];
  /** Sperrzeit eines Inhabers („Nicht da", 16.10.2026) statt eines Einsatzes — nur dann gibt es „trotzdem". */
  sperre?: boolean;
  ganztags?: boolean;
};
export type KonfliktLoesung = { slot_id: string; art: KonfliktArt };
export type KonfliktAntwort = { loesungen: KonfliktLoesung[]; ohne: string[] };

/** Die Konflikte aus einem 409 — sonst null (dann ist es ein anderer Fehler). */
export function konflikteAus(e: unknown): Konflikt[] | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const d = (e.body as { detail?: { konflikte?: unknown } } | null)?.detail;
  const k = d && typeof d === "object" ? d.konflikte : null;
  return Array.isArray(k) && k.length ? (k as Konflikt[]) : null;
}

const zeit = (iso: string) => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
const ms = (iso: string) => new Date(iso).getTime();

function dauer(von: string, bis: string): string {
  const min = Math.round((ms(bis) - ms(von)) / 60000);
  return min < 90 ? `${min} Min.` : `${(min / 60).toLocaleString("de-DE", { maximumFractionDigits: 2 })} Std.`;
}

const tag = (d: Date) => d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });

export function tagUndZeit(k: Konflikt): string {
  if (k.ganztags) {
    const a = new Date(k.start);
    const b = new Date(ms(k.end) - 1000);
    return a.toDateString() === b.toDateString() ? `${tag(a)} ganztägig` : `${tag(a)} bis ${tag(b)}`;
  }
  return `${tag(new Date(k.start))} ${zeit(k.start)}–${zeit(k.end)}`;
}

export function wahlText(k: Konflikt, w: KonfliktArt): string {
  const bez = k.bezeichnung || "anderer Termin";
  const name = k.person_name || "die Person";
  const ns = k.neu_start > k.start ? k.neu_start : k.start;
  const ne = k.neu_end < k.end ? k.neu_end : k.end;
  switch (w) {
    case "alt_luecke": return `${zeit(ns)}–${zeit(ne)} aus „${bez}“ ausklammern (−${dauer(ns, ne)})`;
    case "alt_ende": return `„${bez}“ schon um ${zeit(k.neu_start)} beenden`;
    case "alt_anfang": return `„${bez}“ erst um ${zeit(k.neu_end)} beginnen`;
    case "neu_luecke": return `${zeit(k.start)}–${zeit(k.end)} aus dem neuen Termin ausklammern (−${dauer(k.start, k.end)})`;
    case "neu_ende": return `Im neuen Termin endet ${name} um ${zeit(k.start)}`;
    case "neu_anfang": return `Im neuen Termin beginnt ${name} erst um ${zeit(k.end)}`;
    case "doppelt": return "Beides stehen lassen";
    case "trotzdem": return "Trotzdem einplanen";
  }
}

/** Was die Oberfläche anbietet: nur echte Aufteilungen — kein „doppelt", kein „ohne" (04.10.2026).
 *  Bei einer Sperrzeit kommt „trotzdem" dazu: eine Abwesenheit ist verhandelbar (16.10.2026). */
export function aufteilungen(k: Konflikt): KonfliktArt[] {
  return k.moeglich.filter((a) => a !== "doppelt");
}

/** Vorgewählt: der kürzere Termin wird aus dem längeren ausgeklammert. */
export function standardWahl(k: Konflikt): KonfliktArt {
  const m = aufteilungen(k);
  if (m.includes("alt_luecke")) return "alt_luecke";
  if (m.includes("neu_luecke")) return "neu_luecke";
  const altLaenger = ms(k.end) - ms(k.start) >= ms(k.neu_end) - ms(k.neu_start);
  const reihe: KonfliktArt[] = altLaenger
    ? ["alt_ende", "alt_anfang", "neu_ende", "neu_anfang"]
    : ["neu_ende", "neu_anfang", "alt_ende", "alt_anfang"];
  return reihe.find((a) => m.includes(a)) || m[0] || "alt_luecke";
}

function zusammen(a: KonfliktAntwort | null, b: KonfliktAntwort): KonfliktAntwort {
  const loesungen = [...(a?.loesungen || []).filter((l) => !b.loesungen.some((x) => x.slot_id === l.slot_id)), ...b.loesungen];
  return { loesungen, ohne: Array.from(new Set([...(a?.ohne || []), ...b.ohne])) };
}

/** Den Rumpf einer Termin-Anfrage um die Antwort ergänzen. */
export function mitAntwort<B extends { slots?: { person_id: string }[]; konflikt_loesungen?: KonfliktLoesung[] }>(
  body: B, a: KonfliktAntwort | null,
): B {
  if (!a) return body;
  return {
    ...body,
    ...(body.slots ? { slots: body.slots.filter((s) => !a.ohne.includes(s.person_id)) } : {}),
    konflikt_loesungen: a.loesungen,
  } as B;
}

/**
 * Senden, bei Konflikten fragen, noch einmal senden. `null` = abgebrochen.
 * Höchstens dreimal: ändert sich zwischen Frage und Antwort etwas, fragt der
 * Server neu — eine Endlosschleife soll daraus nicht werden.
 */
export async function mitKonflikten<T>(
  senden: (a: KonfliktAntwort | null) => Promise<T>,
  fragen: (k: Konflikt[]) => Promise<KonfliktAntwort | null>,
): Promise<T | null> {
  let antwort: KonfliktAntwort | null = null;
  for (let runde = 0; ; runde++) {
    try {
      return await senden(antwort);
    } catch (e) {
      const k = konflikteAus(e);
      if (!k || runde >= 2) throw e;
      const neu = await fragen(k);
      if (!neu) return null;
      antwort = zusammen(antwort, neu);
    }
  }
}
